import type { BankTransferDetails } from '@dosfilos/domain';
import { callable } from '../callable';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface InstallmentDto {
  id: string;
  label: string;
  dueDate: string;
  amount: number;
  type: 'enrollment' | 'monthly';
}

export interface InstallmentSummaryItem {
  installmentId: string;
  label: string;
  dueDate: string;
  amount: number;
  paidAmount: number;
  pendingAmount: number; // from pending_verification payments
  /** `installmentState` (domain): overdue from the day after the due date in the
   *  seminary's zone, even when partly paid. */
  status: 'paid' | 'partial' | 'overdue' | 'pending';
  /** amount − paid (never negative). */
  balance: number;
  /** The balance when overdue, else 0. */
  overdueAmount: number;
}

/** Shape returned by GetStudentPaymentSummaryUseCase */
export interface PaymentSummary {
  plan: {
    id: string;
    currency: string;
    termId: string;
    programId: string;
    status: string;
    installments: InstallmentDto[];
    exemptionReason?: string;
    exemptedBy?: string;
    exemptedAt?: string;
    discountAmount?: number;
    discountReason?: string;
    discountedBy?: string;
    discountedAt?: string;
    adminScholarshipGrant?: {
      enrollmentDiscountPercent: number;
      tuitionDiscountPercent: number;
      enrollmentDiscountAmount: number;
      tuitionDiscountAmount: number;
      reason: string;
      grantedBy: string;
      grantedAt: string;
      /** Origin of the grant. Absent = 'manual' (legacy grants predate the field). */
      source?: 'manual' | 'application' | 'program_award';
      sourceRef?: string;
    };
  };
  payments: Array<{
    id: string;
    amount: number;
    paymentDate: string;
    method: string;
    reference: string;
    receiptUrl?: string;
    status: string;
    rejectionReason?: string;
    /** Which cuotas this payment was imputed to, and for how much. The server
     *  has always sent it (`toSnapshot()` spreads every prop); this type just
     *  didn't declare it, so the UI was guessing the payment→cuota link. */
    allocations?: Array<{ installmentId: string; amount: number }>;
  }>;
  installmentSummary: InstallmentSummaryItem[];
  totalDue: number;
  totalPaid: number;
  balance: number;
  overdueCount: number;
  /** Sum of the overdue cuotas' balances. */
  overdueAmount: number;
}

export interface SubmitPaymentReceiptRequest {
  tenantId: string;
  tuitionPlanId: string;
  studentId: string;
  amount: number;
  paymentDate: string;
  method?: 'transfer' | 'cash' | 'check' | 'online';
  reference: string;
  receiptUrl?: string;
  allocations?: Array<{ installmentId: string; amount: number }>;
  notes?: string;
}

export interface VerifyPaymentRequest {
  tenantId: string;
  paymentId: string;
  action: 'verify' | 'reject';
  rejectionReason?: string;
  /**
   * Corrected allocations applied at verify time. Only reduces/removes the
   * student-declared allocations to match the receipt. Omit to verify as declared.
   */
  correctedAllocations?: Array<{ installmentId: string; amount: number }>;
}

// ─── Tuition API ─────────────────────────────────────────────────────────────

export const tuitionApi = {
  getStudentPaymentSummary: async (params: {
    tenantId: string;
    studentId: string;
    termId: string;
  }): Promise<PaymentSummary | null> => {
    const fn = callable<typeof params, PaymentSummary | null>('getStudentPaymentSummary');
    const result = await fn(params);
    return result.data;
  },

  /** SEC-3: los datos de transferencia del seminario (null = sin configurar). */
  getPaymentInstructions: async (params: {
    tenantId: string;
  }): Promise<{ bankTransfer: BankTransferDetails | null }> => {
    const fn = callable<typeof params, { bankTransfer: BankTransferDetails | null }>(
      'getPaymentInstructions',
    );
    return (await fn(params)).data;
  },

  submitPaymentReceipt: async (data: SubmitPaymentReceiptRequest): Promise<{ id: string }> => {
    const fn = callable<SubmitPaymentReceiptRequest, { id: string }>('submitPaymentReceipt');
    const result = await fn(data);
    return result.data;
  },

  verifyPayment: async (data: VerifyPaymentRequest): Promise<{ id: string }> => {
    const fn = callable<VerifyPaymentRequest, { id: string }>('verifyPayment');
    const result = await fn(data);
    return result.data;
  },

  updateTuitionPlanInstallments: async (params: {
    tenantId: string;
    planId: string;
    installments: InstallmentDto[];
    isAdminOverride?: boolean;
  }): Promise<{ success: boolean }> => {
    const fn = callable<typeof params, { success: boolean }>('updateTuitionPlanInstallments');
    const result = await fn(params);
    return result.data;
  },

  deleteTuitionPlan: async (params: {
    tenantId: string;
    planId: string;
    /** Admin exception: also deletes all associated payments */
    forceDelete?: boolean;
  }): Promise<{ success: boolean }> => {
    const fn = callable<typeof params, { success: boolean }>('deleteTuitionPlan');
    const result = await fn(params);
    return result.data;
  },

  changePaymentPlan: async (params: {
    tenantId: string;
    planId: string;
    newPaymentPlanOption: '3-months' | '6-months' | '9-months' | 'full';
    studentId?: string; // required for student self-service calls
    adminMode?: boolean; // set to true for admin calls (skips ownership check)
  }): Promise<{ planId: string; totalAmount: number; installmentCount: number }> => {
    const fn = callable<
      typeof params,
      { planId: string; totalAmount: number; installmentCount: number }
    >('changeStudentPaymentPlan');
    const result = await fn(params);
    return result.data;
  },

  applyTuitionExemption: async (params: {
    tenantId: string;
    planId: string;
    reason: string;
  }): Promise<{ success: boolean; planId: string }> => {
    const fn = callable<typeof params, { success: boolean; planId: string }>(
      'applyTuitionExemption',
    );
    const result = await fn(params);
    return result.data;
  },

  applyTuitionDiscount: async (params: {
    tenantId: string;
    planId: string;
    amount: number;
    reason: string;
  }): Promise<{ success: boolean; planId: string }> => {
    const fn = callable<typeof params, { success: boolean; planId: string }>(
      'applyTuitionDiscount',
    );
    const result = await fn(params);
    return result.data;
  },

  adjustPayment: async (params: {
    tenantId: string;
    paymentId: string;
    deductionAmount: number;
    targetInstallmentId: string;
    reason: string;
  }): Promise<{ paymentId: string; newAmount: number; totalAdjustment: number }> => {
    const fn = callable<
      typeof params,
      { paymentId: string; newAmount: number; totalAdjustment: number }
    >('adjustPayment');
    const result = await fn(params);
    return result.data;
  },

  deletePayment: async (params: {
    tenantId: string;
    paymentId: string;
    /** Student ID of the caller — used for ownership check on the backend. */
    studentId: string;
    /** When true, admin-level override bypasses the status restriction. */
    isAdminOverride?: boolean;
  }): Promise<{ success: boolean }> => {
    const fn = callable<typeof params, { success: boolean }>('deletePayment');
    const result = await fn(params);
    return result.data;
  },

  applyAdminScholarship: async (params: {
    tenantId: string;
    planId: string;
    enrollmentDiscountPercent: number;
    tuitionDiscountPercent: number;
    reason: string;
  }): Promise<{ success: boolean; planId: string }> => {
    const fn = callable<typeof params, { success: boolean; planId: string }>(
      'applyAdminScholarshipGrant',
    );
    const result = await fn(params);
    return result.data;
  },
};
